import { toast } from 'react-toastify';
import React, { useState } from 'react';
import { IDataCatalog, releaseDataCatalogVersion, submitDataCatalog, VerdocsEndpoint } from '@verdocs/js-sdk';
import { Button, Spinner } from '../Components';
import { Dialog } from './Dialog';

interface SubmitDataCatalogDialogProps {
  catalog: IDataCatalog;
  /** The version to submit. */
  version: number;
  /** Release the current keys as that version first. */
  release: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

export function SubmitDataCatalogDialog({ catalog, version, release, onClose, onSubmitted }: SubmitDataCatalogDialogProps) {
  const [ notes, setNotes ] = useState('');
  const [ submitting, setSubmitting ] = useState(false);

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }

    const endpoint = VerdocsEndpoint.getDefault();
    setSubmitting(true);
    try {
      if (release) {
        await releaseDataCatalogVersion(endpoint, catalog.organization_id, catalog.id);
      }
      await submitDataCatalog(endpoint, catalog.organization_id, catalog.id, notes.trim() || undefined);
      toast.success(`Submitted version ${version} for review`);
      onSubmitted();
    } catch (e: any) {
      setSubmitting(false);
      toast.error(`Error submitting the catalog: ${e.response?.data?.message || e.message}`);
    }
  };

  return (
    <Dialog onCancel={onClose}>
      <div className="text-lg font-medium text-[#33364b]">Submit to the public marketplace</div>
      <div className="mb-4 mt-0.5 text-[13px] text-gray-500">
        {release && `This releases version ${version} with the catalog's current keys. `}
        Verdocs reviews version {version} of {catalog.name}. Once approved, every organization can find it in the marketplace and import a copy.
        Later versions stay private until you submit them too.
      </div>

      <label className="mb-1 block text-[13px] font-medium text-[#33364b]">Notes for the reviewer</label>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        rows={4}
        placeholder="Field vocabulary for homeowners and auto applications"
        className="w-full rounded-md border border-[#dcdee5] p-3 text-[13px] text-[#33364b] outline-none placeholder:text-gray-400 focus:border-[#654dcb]"
      />

      <div className="mt-4 flex flex-row items-center justify-end gap-2 border-t border-[#f2f3f5] pt-4">
        {submitting && <Spinner mode="dark" />}
        <Button
          type="button"
          size="small"
          variant="secondary"
          label="Cancel"
          onClick={onClose}
          disabled={submitting}
        />
        <Button
          size="small"
          label={`Submit version ${version}`}
          onClick={handleSubmit}
          disabled={submitting}
        />
      </div>
    </Dialog>
  );
}
